import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { XCircle, Scale, ChevronDown, ChevronRight, AlertTriangle, RotateCcw } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from 'date-fns';
import IssueAppealPanel from './IssueAppealPanel';

const REJECTION_REASON_LABELS = {
  too_confusing: 'Too Confusing',
  missing_context: 'Missing Context',
  unclear_problem: 'Unclear Problem',
  no_impact_evidence: 'No Impact Evidence',
  duplicate_issue: 'Duplicate',
  not_actionable: 'Not Actionable',
  insufficient_detail: 'Insufficient Detail',
};

export default function RejectedIssuesSection({ issues, officers }) {
  const [expanded, setExpanded] = useState(false);
  const [appealIssue, setAppealIssue] = useState(null);

  const rejectedIssues = issues.filter(i => i.status === 'rejected');
  const appealedIssues = issues.filter(i => i.appeal_status === 'appealed');

  if (rejectedIssues.length === 0 && appealedIssues.length === 0) return null;

  // Rejection pattern analysis
  const reasonCounts = {};
  const reporterCounts = {};
  rejectedIssues.forEach(issue => {
    if (issue.rejection_reason) {
      reasonCounts[issue.rejection_reason] = (reasonCounts[issue.rejection_reason] || 0) + 1;
    }
    if (issue.reported_by) {
      reporterCounts[issue.reported_by] = (reporterCounts[issue.reported_by] || 0) + 1;
    }
  });

  const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0];

  return (
    <>
      <Card className="glass-panel border-red-500/20">
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger className="w-full">
            <CardHeader className="py-3 cursor-pointer hover:bg-slate-800/30 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-red-400 flex items-center gap-2">
                  {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <XCircle className="w-4 h-4" />
                  Rejected Issues
                  <Badge className="bg-red-500/20 text-red-300 text-[10px] ml-1">{rejectedIssues.length}</Badge>
                  {appealedIssues.length > 0 && (
                    <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">{appealedIssues.length} appealed</Badge>
                  )}
                </CardTitle>
                {topReason && (
                  <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-600">
                    Top: {REJECTION_REASON_LABELS[topReason[0]]} ({topReason[1]})
                  </Badge>
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-2">
              {/* Pending Appeals First */}
              {appealedIssues.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Scale className="w-3 h-3" /> Pending Appeals
                  </p>
                  {appealedIssues.map(issue => {
                    const reporter = officers.find(o => o.user_email === issue.reported_by);
                    return (
                      <div key={issue.id} className="flex items-center gap-2 p-2 rounded bg-amber-950/20 border border-amber-500/20 mb-1">
                        <Scale className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-200 font-medium truncate">{issue.title}</p>
                          <p className="text-[10px] text-amber-400/70 truncate">{issue.appeal_reason}</p>
                        </div>
                        <Button data-testid="hc-review-btn" size="sm" variant="outline" onClick={() => setAppealIssue(issue)}
                          className="h-6 text-[10px] border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                          Review
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Rejected Issues */}
              {rejectedIssues.map(issue => {
                const reporter = officers.find(o => o.user_email === issue.reported_by);
                const rejector = officers.find(o => o.user_email === issue.rejected_by);
                return (
                  <div key={issue.id} className="flex items-center gap-2 p-2 rounded bg-slate-800/30">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-300 truncate">{issue.title}</p>
                      <p className="text-[10px] text-slate-500">
                        By {reporter?.callsign || issue.reported_by}
                        {issue.rejected_at && ` • Rejected ${format(new Date(issue.rejected_at), 'MMM d')}`}
                        {rejector && ` by ${rejector.callsign}`}
                      </p>
                    </div>
                    <Badge className="bg-red-500/15 text-red-300 text-[10px] flex-shrink-0">
                      {REJECTION_REASON_LABELS[issue.rejection_reason] || 'Rejected'}
                    </Badge>
                    {issue.appeal_status !== 'none' && (
                      <Badge className={`text-[10px] flex-shrink-0 ${
                        issue.appeal_status === 'appeal_accepted' ? 'bg-emerald-500/15 text-emerald-300' :
                        issue.appeal_status === 'appeal_denied' ? 'bg-slate-500/15 text-slate-400' :
                        'bg-amber-500/15 text-amber-300'
                      }`}>
                        {issue.appeal_status === 'appeal_accepted' ? 'Reinstated' :
                         issue.appeal_status === 'appeal_denied' ? 'Appeal Denied' : 'Appealed'}
                      </Badge>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setAppealIssue(issue)}
                      className="h-6 text-[10px] text-slate-400 hover:text-slate-200 px-1.5">
                      <Scale className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <IssueAppealPanel
        issue={appealIssue}
        officers={officers}
        open={!!appealIssue}
        onClose={() => setAppealIssue(null)}
      />
    </>
  );
}